import LegalFooter from "./components/LegalFooter";

export default function PrivacyPolicy() {
  return (
    <div style={s.scrollPage}>
      {/* HEADER */}
      <div style={s.header}>
        <div style={s.title}>Privacy Policy</div>
        <div style={s.subTitle}>
          SpeedDigits Trainer • Last Updated: January 2026
        </div>
      </div>

      {/* INTRO */}
      <p style={s.intro}>
        SpeedDigits Trainer (“the Application”, “we”, “our”, or “us”) is a
        web-based arithmetic training application designed for personal
        learning, practice, and performance analysis. This Privacy Policy
        explains how information is handled when using the Application in both
        Guest and Registered (Logged-in) modes.
      </p>

      {/* SECTION 1 */}
      <div style={s.divider} />
      <h2 style={s.sectionTitle}>1. Data Collection Overview</h2>
      <p style={s.text}>
        The Application supports two access modes: Guest access and Registered
        (Logged-in) access.
      </p>
      <p style={s.text}>
        Data handling depends on the selected access mode and enabled features.
        Information may be stored temporarily for Guest users and may be stored
        persistently for Logged-in users to support account-based functionality.
      </p>

      {/* SECTION 2 */}
      <div style={s.divider} />
      <h2 style={s.sectionTitle}>2. Information That May Be Collected</h2>

      <h3 style={s.subSectionTitle}>2.1 Profile Information</h3>
      <p style={s.text}>
        To support personalization and learning experience, the Application may
        request the following profile information.
      </p>

      <ul style={s.list}>
        <li>
          <strong>Required fields:</strong> Name, Gender, Class or learning
          level
        </li>
        <li>
          <strong>Optional fields:</strong> Date of birth or age, Email address,
          User description or notes, Profile photo
        </li>
      </ul>

      <p style={s.text}>
        For Logged-in users, provided profile information may be associated with
        the user’s account. For Guest users, profile information is used for the
        current session and may not persist.
      </p>

      <h3 style={s.subSectionTitle}>2.2 Practice & Performance Data</h3>
      <ul style={s.list}>
        <li>Test names and timestamps</li>
        <li>Selected operations and configurations</li>
        <li>Total questions attempted</li>
        <li>Correct and incorrect responses</li>
        <li>Accuracy percentages</li>
        <li>Average and total response time</li>
        <li>Stream test outcomes</li>
      </ul>

      <h3 style={s.subSectionTitle}>2.3 Analysis & Visualization Data</h3>
      <ul style={s.list}>
        <li>Accuracy and performance graphs</li>
        <li>Correct vs incorrect statistics</li>
        <li>Derived performance trends</li>
      </ul>

      <h3 style={s.subSectionTitle}>2.4 Application Preferences</h3>
      <ul style={s.list}>
        <li>App configuration and appearance settings</li>
        <li>Feature preferences and filters</li>
      </ul>

      {/* SECTION 3 */}
      <div style={s.divider} />
      <h2 style={s.sectionTitle}>3. Guest Mode Data Handling</h2>
      <p style={s.text}>When using the Application in Guest mode:</p>
      <ul style={s.list}>
        <li>No account or login is required</li>
        <li>Profile data is used only for the active session</li>
        <li>Progress may not persist across sessions or devices</li>
      </ul>
      <p style={s.text}>
        The Application does not guarantee data retention in Guest mode.
      </p>

      {/* SECTION 4 */}
      <div style={s.divider} />
      <h2 style={s.sectionTitle}>4. Logged-in User Data Handling</h2>
      <p style={s.text}>
        When logged in, the Application may store user data to support features
        such as saved history, analysis, and personalization.
      </p>
      <ul style={s.list}>
        <li>Data may be associated with the user’s account</li>
        <li>Data may persist across sessions or devices</li>
        <li>Account-based features require data persistence</li>
      </ul>

      {/* SECTION 5 */}
      <div style={s.divider} />
      <h2 style={s.sectionTitle}>5. User Control & Data Deletion</h2>
      <p style={s.text}>Users have full control over their data and may:</p>
      <ul style={s.list}>
        <li>Reset analysis data only</li>
        <li>Delete selected test history</li>
        <li>Clear all stored data, including account-related information</li>
      </ul>
      <p style={s.text}>
        Once deleted, data is permanently removed and cannot be recovered.
      </p>

      {/* SECTION 6 */}
      <div style={s.divider} />
      <h2 style={s.sectionTitle}>6. Data Export</h2>
      <ul style={s.list}>
        <li>Export test history as CSV or Excel files</li>
        <li>Download exported files to the user’s device</li>
      </ul>
      <p style={s.text}>
        Exported files are generated for user convenience and are not retained
        by the Application after download.
      </p>

      {/* SECTION 7 */}
      <div style={s.divider} />
      <h2 style={s.sectionTitle}>7. Data Sharing</h2>
      <ul style={s.list}>
        <li>We do not sell or rent user data</li>
        <li>No third-party advertising data is collected</li>
        <li>No hidden tracking or profiling is performed</li>
      </ul>

      {/* SECTION 8 */}
      <div style={s.divider} />
      <h2 style={s.sectionTitle}>8. Data Security</h2>
      <ul style={s.list}>
        <li>Reasonable measures are used to protect stored data</li>
        <li>Access controls are applied where applicable</li>
        <li>No system can be guaranteed 100% secure</li>
      </ul>
      <p style={s.text}>
        Users are responsible for securing their own devices and login
        credentials.
      </p>

      {/* SECTION 9 */}
      <div style={s.divider} />
      <h2 style={s.sectionTitle}>9. Children & Educational Use</h2>
      <p style={s.text}>
        The Application is intended for educational use. Parents or guardians
        are responsible for supervising usage by minors.
      </p>

      {/* SECTION 10 */}
      <div style={s.divider} />
      <h2 style={s.sectionTitle}>10. Policy Updates</h2>
      <p style={s.text}>
        This Privacy Policy may be updated to reflect feature changes, system
        improvements, or legal requirements. Updates will be indicated by a
        change to the “Last Updated” date.
      </p>

      {/* SECTION 11 */}
      <div style={s.divider} />
      <h2 style={s.sectionTitle}>11. Contact</h2>
      <p style={s.text}>
        For privacy-related questions, users may use the Contact section
        available within the Application.
      </p>

      <LegalFooter styles={s} />
    </div>
  );
}

/* ================= STYLES (UNCHANGED) ================= */

const s = {
  scrollPage: {
    height: "calc(100vh - var(--topbar-height))",
    overflowY: "auto",
    padding: "40px 24px 80px",
  },

  page: {
    maxWidth: 960,
    margin: "0 auto",
    padding: "40px 16px 80px",
  },

  header: {
    textAlign: "center",
    marginBottom: 40,
  },

  title: {
    fontSize: "clamp(32px, 6vw, 52px)",
    fontWeight: 800,
    background: "linear-gradient(90deg,#9333ea,#ec4899)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },

  subTitle: {
    marginTop: 10,
    fontSize: 14,
    opacity: 0.7,
  },

  intro: {
    textAlign: "center",
    fontSize: 16,
    lineHeight: 1.8,
    opacity: 0.85,
    maxWidth: 760,
    margin: "0 auto 40px",
  },

  divider: {
    width: 240,
    margin: "44px auto",
    borderBottom: "2px dotted rgba(147,51,234,0.55)",
  },

  sectionTitle: {
    textAlign: "center",
    fontSize: 22,
    fontWeight: 700,
    color: "#a855f7",
    marginBottom: 16,
  },

  subSectionTitle: {
    fontSize: 17,
    fontWeight: 600,
    color: "#c084fc",
    margin: "22px 0 10px",
  },

  text: {
    fontSize: 15,
    lineHeight: 1.8,
    marginBottom: 14,
  },

  list: {
    paddingLeft: 22,
    marginBottom: 16,
    lineHeight: 1.9,
  },
};
